import React from 'react';
import { Loader2, HardDrive, PlugZap } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export type BackupFrequencia = 'DIARIA' | 'SEMANAL' | 'MENSAL';
export type BackupDestinoTipo = 'LOCAL' | 'REMOTO_SSH';
export type BackupAutenticacaoTipo = 'SENHA' | 'CHAVE_PRIVADA';

export interface ConfiguracaoBackupState {
  ativo: boolean;
  frequencia: BackupFrequencia;
  diaSemana: number | null;
  diaMes: number | null;
  horario: string;
  backupMidias: boolean;
  midiasGrupos: boolean;
  destinoTipo: BackupDestinoTipo;
  diretorioLocal: string;
  remotoHost: string;
  remotoPorta: number | null;
  remotoUsuario: string;
  remotoAutenticacao: BackupAutenticacaoTipo;
  remotoSenha: string;
  remotoChavePrivada: string;
  remotoDiretorio: string;
  ultimoBackupEm?: string | null;
  ultimoBackupStatus?: 'SUCESSO' | 'ERRO' | null;
  ultimoBackupMensagem?: string | null;
}

export const defaultConfiguracaoBackup: ConfiguracaoBackupState = {
  ativo: false,
  frequencia: 'DIARIA',
  diaSemana: 1,
  diaMes: 1,
  horario: '03:00',
  backupMidias: false,
  midiasGrupos: false,
  destinoTipo: 'LOCAL',
  diretorioLocal: '',
  remotoHost: '',
  remotoPorta: 22,
  remotoUsuario: '',
  remotoAutenticacao: 'SENHA',
  remotoSenha: '',
  remotoChavePrivada: '',
  remotoDiretorio: '',
  ultimoBackupEm: null,
  ultimoBackupStatus: null,
  ultimoBackupMensagem: null,
};

const DIAS_SEMANA = [
  { valor: 0, label: 'Domingo' },
  { valor: 1, label: 'Segunda-feira' },
  { valor: 2, label: 'Terça-feira' },
  { valor: 3, label: 'Quarta-feira' },
  { valor: 4, label: 'Quinta-feira' },
  { valor: 5, label: 'Sexta-feira' },
  { valor: 6, label: 'Sábado' },
];

interface Props {
  value: ConfiguracaoBackupState;
  onChange: (patch: Partial<ConfiguracaoBackupState>) => void;
  isEdicao: boolean;
  testandoConexao?: boolean;
  executandoAgora?: boolean;
  onTestarConexao?: () => void;
  onExecutarAgora?: () => void;
}

const labelCls = 'dark:text-white dark:bg-slate-700';
const inputCls = 'sm:col-span-3 dark:text-white dark:bg-slate-700';
const rowCls = 'grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-1.5 sm:gap-4';

const ConfiguracaoBackupEmpresa: React.FC<Props> = ({
  value,
  onChange,
  isEdicao,
  testandoConexao,
  executandoAgora,
  onTestarConexao,
  onExecutarAgora,
}) => {
  return (
    <div className="sm:col-span-5 border-t border-gray-200 dark:border-slate-600 pt-4 mt-2">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-gray-500 dark:text-gray-300" />
          <h5 className="font-semibold text-gray-600 dark:text-white">Backup Automático</h5>
        </div>
        <Switch checked={value.ativo} onCheckedChange={(checked) => onChange({ ativo: checked })} />
      </div>

      {value.ativo && (
        <div className="flex flex-col gap-3 pl-1">
          <div className={rowCls}>
            <Label className={labelCls}>Frequência</Label>
            <div className="sm:col-span-3">
              <Select value={value.frequencia} onValueChange={(v) => onChange({ frequencia: v as BackupFrequencia })}>
                <SelectTrigger className="dark:text-white dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIARIA">Diária</SelectItem>
                  <SelectItem value="SEMANAL">Semanal</SelectItem>
                  <SelectItem value="MENSAL">Mensal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {value.frequencia === 'SEMANAL' && (
            <div className={rowCls}>
              <Label className={labelCls}>Dia da semana</Label>
              <div className="sm:col-span-3">
                <Select
                  value={String(value.diaSemana ?? 1)}
                  onValueChange={(v) => onChange({ diaSemana: Number(v) })}
                >
                  <SelectTrigger className="dark:text-white dark:bg-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIAS_SEMANA.map((d) => (
                      <SelectItem key={d.valor} value={String(d.valor)}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {value.frequencia === 'MENSAL' && (
            <div className={rowCls}>
              <Label className={labelCls}>Dia do mês</Label>
              <Input
                type="number"
                min={1}
                max={31}
                value={value.diaMes ?? 1}
                onChange={(e) => onChange({ diaMes: Math.min(31, Math.max(1, Number(e.target.value) || 1)) })}
                className={inputCls}
              />
            </div>
          )}

          <div className={rowCls}>
            <Label className={labelCls}>Horário</Label>
            <Input
              type="time"
              value={value.horario}
              onChange={(e) => onChange({ horario: e.target.value })}
              className={inputCls}
            />
          </div>

          <div className="flex items-center justify-between gap-3 p-3 border border-gray-200 dark:border-slate-600 rounded-lg">
            <div>
              <Label className="dark:text-white">Incluir mídias no backup</Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">Além do banco de dados, também copia os arquivos de mídia (imagens, áudios, vídeos, documentos) da empresa.</p>
            </div>
            <Switch checked={value.backupMidias} onCheckedChange={(checked) => onChange({ backupMidias: checked })} />
          </div>

          {value.backupMidias && (
            <div className="flex items-center justify-between gap-3 p-3 border border-gray-200 dark:border-slate-600 rounded-lg ml-4">
              <div>
                <Label className="dark:text-white">Incluir mídias de grupos</Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">Se desativado, somente as mídias de conversas privadas são incluídas no backup.</p>
              </div>
              <Switch checked={value.midiasGrupos} onCheckedChange={(checked) => onChange({ midiasGrupos: checked })} />
            </div>
          )}

          <div className={rowCls}>
            <Label className={labelCls}>Destino do backup</Label>
            <div className="sm:col-span-3">
              <Select value={value.destinoTipo} onValueChange={(v) => onChange({ destinoTipo: v as BackupDestinoTipo })}>
                <SelectTrigger className="dark:text-white dark:bg-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOCAL">Diretório local (neste servidor)</SelectItem>
                  <SelectItem value="REMOTO_SSH">Servidor remoto (via SSH)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {value.destinoTipo === 'LOCAL' ? (
            <div className={rowCls}>
              <Label className={labelCls}>Diretório local</Label>
              <Input
                value={value.diretorioLocal}
                onChange={(e) => onChange({ diretorioLocal: e.target.value })}
                placeholder="/app/backups (padrão se vazio)"
                className={inputCls}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 pl-4 border-l-2 border-gray-200 dark:border-slate-600">
              <div className={rowCls}>
                <Label className={labelCls}>Host</Label>
                <Input
                  value={value.remotoHost}
                  onChange={(e) => onChange({ remotoHost: e.target.value })}
                  placeholder="ex: 192.168.1.10 ou backup.exemplo.com"
                  className={inputCls}
                />
              </div>
              <div className={rowCls}>
                <Label className={labelCls}>Porta</Label>
                <Input
                  type="number"
                  value={value.remotoPorta ?? 22}
                  onChange={(e) => onChange({ remotoPorta: Number(e.target.value) || 22 })}
                  className="sm:col-span-1 dark:text-white dark:bg-slate-700"
                />
              </div>
              <div className={rowCls}>
                <Label className={labelCls}>Usuário</Label>
                <Input
                  value={value.remotoUsuario}
                  onChange={(e) => onChange({ remotoUsuario: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div className={rowCls}>
                <Label className={labelCls}>Autenticação</Label>
                <div className="sm:col-span-3">
                  <Select
                    value={value.remotoAutenticacao}
                    onValueChange={(v) => onChange({ remotoAutenticacao: v as BackupAutenticacaoTipo })}
                  >
                    <SelectTrigger className="dark:text-white dark:bg-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SENHA">Senha</SelectItem>
                      <SelectItem value="CHAVE_PRIVADA">Chave privada SSH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {value.remotoAutenticacao === 'SENHA' ? (
                <div className={rowCls}>
                  <Label className={labelCls}>Senha</Label>
                  <Input
                    type="password"
                    value={value.remotoSenha}
                    onChange={(e) => onChange({ remotoSenha: e.target.value })}
                    placeholder={value.remotoSenha === '***' ? 'Senha já configurada — deixe assim para manter' : ''}
                    className={inputCls}
                  />
                </div>
              ) : (
                <div className={rowCls}>
                  <Label className={labelCls}>Chave privada</Label>
                  <Textarea
                    value={value.remotoChavePrivada}
                    onChange={(e) => onChange({ remotoChavePrivada: e.target.value })}
                    placeholder={value.remotoChavePrivada === '***' ? 'Chave já configurada — deixe assim para manter' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                    className="sm:col-span-3 dark:text-white dark:bg-slate-700 font-mono text-xs"
                    rows={4}
                  />
                </div>
              )}

              <div className={rowCls}>
                <Label className={labelCls}>Diretório remoto</Label>
                <Input
                  value={value.remotoDiretorio}
                  onChange={(e) => onChange({ remotoDiretorio: e.target.value })}
                  placeholder="/home/usuario/backups"
                  className={inputCls}
                />
              </div>

              {isEdicao && onTestarConexao && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onTestarConexao}
                    disabled={testandoConexao}
                    className="dark:text-white dark:bg-slate-700"
                  >
                    {testandoConexao ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                    Testar conexão
                  </Button>
                </div>
              )}
            </div>
          )}

          {isEdicao && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2 p-3 border border-gray-200 dark:border-slate-600 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {value.ultimoBackupEm ? (
                  <>
                    Último backup: <span className={value.ultimoBackupStatus === 'ERRO' ? 'text-red-500' : 'text-green-600 dark:text-green-400'}>
                      {value.ultimoBackupStatus === 'ERRO' ? 'Falhou' : 'Sucesso'}
                    </span> em {new Date(value.ultimoBackupEm).toLocaleString('pt-BR')}
                    {value.ultimoBackupStatus === 'ERRO' && value.ultimoBackupMensagem && (
                      <span className="block text-red-500 mt-1">{value.ultimoBackupMensagem}</span>
                    )}
                  </>
                ) : (
                  'Nenhum backup executado ainda.'
                )}
              </div>
              {onExecutarAgora && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onExecutarAgora}
                  disabled={executandoAgora}
                  className="dark:text-white dark:bg-slate-700 shrink-0 sm:ml-3"
                >
                  {executandoAgora && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Testar backup agora
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfiguracaoBackupEmpresa;
